import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { chargerPolice, useTheme } from "@/context/ThemeContext";
import { api, messageErreur } from "@/lib/api";
import { ThemeMiniature } from "@/components/theme/ThemeMiniature";
import { ChampCouleur, ChampRayon, ChoixSimple, LIBELLES_COULEURS, Section } from "@/components/theme/ChampsTheme";
import { Champ, EchelleTemperature } from "@/components/commun";

const OMBRES = [
  { valeur: "0 1px 2px rgba(40,35,20,.04), 0 8px 24px rgba(40,35,20,.06)", label: "Douce (par défaut)" },
  { valeur: "0 2px 4px rgba(0,0,0,.06), 0 16px 40px rgba(0,0,0,.12)", label: "Marquée" },
  { valeur: "none", label: "Aucune ombre" },
];
const PILULES = [{ valeur: "999px", label: "Pilule complète" }, { valeur: "14px", label: "Arrondi moyen" }, { valeur: "6px", label: "Presque carré" }, { valeur: "0px", label: "Carré" }];
const fondLueur = (glow) => `radial-gradient(900px 520px at 18% -6%, ${glow}, transparent 62%)`;
const luminance = (hex) => {
  const c = /^#[0-9a-f]{6}$/i.test(hex || "") ? [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) : [0, 0, 0];
  const [r, g, b] = c.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contraste = (a, b) => { const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m); return (x + 0.05) / (y + 0.05); };

export default function ThemeEditeur() {
  const { themeId } = useParams();
  const { user } = useAuth();
  const { demarrerApercu, rechargerActif } = useTheme();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(null);
  const [polices, setPolices] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const autorise = user.navigation.parametres.includes("themes");

  useEffect(() => {
    if (!autorise) return;
    api.get(`/themes/${themeId}`).then((r) => setTheme(r.data)).catch((e) => toast.error(messageErreur(e)));
    api.get("/referentiels").then((r) => { setPolices(r.data.polices); r.data.polices.forEach((p) => chargerPolice(p)); });
  }, [themeId, autorise]);

  if (!autorise) return <Navigate to="/parametres/compte" replace />;
  if (!theme) return null;
  const t = theme.tokens;
  const majToken = (cle) => (v) => setTheme((x) => {
    const tk = { ...x.tokens, [cle]: v };
    if (cle === "font-nom") tk.font = `'${v}', sans-serif`;
    if (cle === "glow" && x.tokens["glow-gradient"] === fondLueur(x.tokens.glow)) tk["glow-gradient"] = fondLueur(v);
    return { ...x, tokens: tk };
  });
  const contrasteFaible = contraste(t.ink, t.surface) < 4.5 || contraste(t["accent-ink"], t.accent) < 4.5;

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.patch(`/themes/${themeId}`, { nom: theme.nom, tokens: theme.tokens });
      setTheme(data);
      if (data.statut === "actif") rechargerActif();
      toast.success("Thème enregistré.");
    } catch (e) { toast.error(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const apercu = () => { demarrerApercu(theme); navigate("/"); };

  return (
    <div data-testid="page-theme-editor" className="pb-24 md:pb-0">
      <Link to="/parametres/themes" className="inline-flex items-center gap-2 min-h-[44px] font-semibold text-sm" data-testid="theme-editor-back"><ArrowLeft size={16} />Thèmes</Link>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 mb-5" data-testid="page-title">Modifier « {theme.nom} »</h1>
      <div className="lg:grid lg:grid-cols-[1fr_420px] gap-6 items-start">
        <div className="lg:order-2 lg:sticky lg:top-24 mb-5 lg:mb-0">
          <p className="at-label mb-2">Aperçu en direct</p>
          <ThemeMiniature tokens={t} grand testId="theme-editor-live-preview" />
        </div>
        <div className="space-y-4 lg:order-1 min-w-0">
          <Section titre="Nom">
            <Champ label="Nom du thème" id="theme-nom" value={theme.nom} onChange={(e) => setTheme((x) => ({ ...x, nom: e.target.value }))} data-testid="theme-name-input" />
          </Section>
          <Section titre="Couleurs">
            {contrasteFaible && <p className="text-sm font-semibold mb-3 at-soft p-3" style={{ color: "var(--danger)" }} data-testid="theme-contrast-warning">Contraste insuffisant : le texte risque d'être illisible (texte sur cartes ou texte sur accent).</p>}
            <div className="grid sm:grid-cols-2 gap-2">{Object.keys(LIBELLES_COULEURS).map((k) => <ChampCouleur key={k} cle={k} valeur={t[k]} onChange={majToken(k)} />)}</div>
          </Section>
          <Section titre="Police">
            <ChoixSimple label="Police (Google Fonts, 400 à 700)" valeur={t["font-nom"]} onChange={majToken("font-nom")} testId="theme-font-select"
              options={polices.map((p) => ({ valeur: p, label: p, style: { fontFamily: p === "Système" ? "'Helvetica Neue', 'Segoe UI', system-ui, sans-serif" : `'${p}', sans-serif` } }))} />
          </Section>
          <Section titre="Arrondis">
            <div className="grid sm:grid-cols-2 gap-2">
              <ChampRayon label="Cartes" valeur={t["radius-card"]} onChange={majToken("radius-card")} testId="theme-radius-card" />
              <ChampRayon label="Cartes de fiche" valeur={t["radius-fiche"]} onChange={majToken("radius-fiche")} testId="theme-radius-fiche" />
              <ChampRayon label="Champs de saisie" valeur={t["radius-control"]} onChange={majToken("radius-control")} max={30} testId="theme-radius-control" />
              <ChoixSimple label="Pilules et boutons" valeur={t["radius-pill"]} onChange={majToken("radius-pill")} options={PILULES} testId="theme-radius-pill" />
            </div>
          </Section>
          <Section titre="Ombre et fond de page">
            <div className="space-y-3">
              <ChoixSimple label="Ombre des cartes" valeur={OMBRES.some((o) => o.valeur === t.shadow) ? t.shadow : OMBRES[0].valeur} onChange={majToken("shadow")} options={OMBRES} testId="theme-shadow-select" />
              <ChoixSimple label="Fond de page" valeur={t["glow-gradient"] === "none" ? "none" : "lueur"} testId="theme-background-select"
                onChange={(v) => majToken("glow-gradient")(v === "none" ? "none" : fondLueur(t.glow))}
                options={[{ valeur: "lueur", label: "Lueur en haut à gauche (couleur « Lueur »)" }, { valeur: "none", label: "Uni, sans lueur" }]} />
              {t["glow-gradient"] !== "none" && t["glow-gradient"] !== fondLueur(t.glow) && (
                <button type="button" className="at-btn at-btn-ghost text-sm" onClick={() => majToken("glow-gradient")(fondLueur(t.glow))} data-testid="theme-sync-glow">Utiliser la couleur « Lueur » actuelle</button>
              )}
            </div>
          </Section>
          <Section titre="Températures">
            <p className="text-sm at-muted mb-3">Couleurs fixes, communes à tous les thèmes : non modifiables.</p>
            <EchelleTemperature />
          </Section>
        </div>
      </div>
      <div className="fixed md:static bottom-[68px] inset-x-0 px-4 md:px-0 pt-3 pb-3 md:p-0 md:mt-6 flex gap-2 z-30 backdrop-blur-xl md:backdrop-blur-none" style={{ background: "color-mix(in srgb, var(--bg) 82%, transparent)" }}>
        <button type="button" onClick={apercu} className="at-btn at-btn-ghost flex-1 md:flex-none" style={{ boxShadow: "var(--shadow)" }} data-testid="theme-editor-preview"><Eye size={17} />Aperçu</button>
        <button type="button" onClick={enregistrer} disabled={envoi} className="at-btn at-btn-primary flex-1 md:flex-none" data-testid="theme-editor-save"><Save size={17} />{envoi ? "Enregistrement…" : "Enregistrer"}</button>
      </div>
    </div>
  );
}
